import { Injectable, OnDestroy } from '@angular/core';
import { CommonService } from './common.service';

@Injectable({ providedIn: 'root' })
export class UserActivityTrackerService implements OnDestroy {

  private readonly CHANNEL = 'user-active-channel';
  private readonly STORAGE_KEY = 'user-last-active-ts';
  private _broadcastChannel: BroadcastChannel | null = null;
  private _timerId: any;
  private _intervalInSecondToLogActivity: number = 5 * 60; // 5 minutes

  constructor(private readonly _commonService: CommonService) {
    // 1) Set up cross-tab messaging
    if ('BroadcastChannel' in window) {
      this._broadcastChannel = new BroadcastChannel(this.CHANNEL);
      this._broadcastChannel.onmessage = ({
        data: ts,
      }: MessageEvent<number>) => {
        // another tab just updated—reset our timer
        localStorage.setItem(this.STORAGE_KEY, ts.toString());
        this.scheduleNext();
      };
    } else {
      // fallback for older browsers
      (window as Window & typeof globalThis).addEventListener(
        'storage',
        (e: StorageEvent) => {
          if (e.key === this.STORAGE_KEY && e.newValue) {
            this.scheduleNext();
          }
        }
      );
    }

    // 2) If no last-active ts, call immediately; otherwise schedule normally
    const lastTs = Number(localStorage.getItem(this.STORAGE_KEY) || '0');
    if (lastTs === 0) {
      this.updateLastActive();
    } else {
      this.scheduleNext();
    }
  }

  private scheduleNext() {
    if (this._timerId) {
      clearTimeout(this._timerId);
    }

    const lastTs = Number(localStorage.getItem(this.STORAGE_KEY) || '0');
    const now = Date.now();
    const interval = this._intervalInSecondToLogActivity * 1000; // 5 minutes

    // If it’s already been ≥5 min, call immediately; else wait the remainder
    const delay = now - lastTs >= interval ? 0 : interval - (now - lastTs);

    // add a small random jitter (0–10 s) to avoid “thundering herd”
    const jitter = Math.random() * 10_000;

    this._timerId = setTimeout(() => this.updateLastActive(), delay + jitter);
  }

  private updateLastActive() {
    this._commonService.updateUserLastActive().subscribe(() => {
      const ts = Date.now();
      // persist for other tabs
      localStorage.setItem(this.STORAGE_KEY, ts.toString());
      // notify via BroadcastChannel
      this._broadcastChannel?.postMessage(ts);
      // schedule next run
      this.scheduleNext();
    });
  }

  ngOnDestroy() {
    this._broadcastChannel?.close();
    if (this._timerId) {
      clearTimeout(this._timerId);
    }
  }
}
